import * as THREE from 'three';
import { CONFIG } from '../config';
import type { GameState } from '../state/game-state';

const vert = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying vec3 vWorldPos;
  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPos = worldPos.xyz;
    vNormal = normalize(normalMatrix * normal);
    vViewDir = normalize(cameraPosition - worldPos.xyz);
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

const frag = /* glsl */ `
  uniform float uTime;
  uniform vec3 uCenterColor;
  uniform vec3 uMidColor;
  uniform vec3 uOuterColor;
  uniform float uCorruption;
  uniform float uRespawnProgress;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying vec3 vWorldPos;

  vec3 hash3(vec3 p) {
    p = vec3(dot(p, vec3(127.1, 311.7, 74.7)),
             dot(p, vec3(269.5, 183.3, 246.1)),
             dot(p, vec3(113.5, 271.9, 124.6)));
    return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
  }

  float noise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(mix(dot(hash3(i + vec3(0,0,0)), f - vec3(0,0,0)),
              dot(hash3(i + vec3(1,0,0)), f - vec3(1,0,0)), f.x),
          mix(dot(hash3(i + vec3(0,1,0)), f - vec3(0,1,0)),
              dot(hash3(i + vec3(1,1,0)), f - vec3(1,1,0)), f.x), f.y),
      mix(mix(dot(hash3(i + vec3(0,0,1)), f - vec3(0,0,1)),
              dot(hash3(i + vec3(1,0,1)), f - vec3(1,0,1)), f.x),
          mix(dot(hash3(i + vec3(0,1,1)), f - vec3(0,1,1)),
              dot(hash3(i + vec3(1,1,1)), f - vec3(1,1,1)), f.x), f.y),
      f.z);
  }

  void main() {
    float fres = pow(1.0 - max(dot(vNormal, vViewDir), 0.0), 2.0);
    float n = noise(vWorldPos * 1.5 + uTime * 0.3);
    float radial = 1.0 - smoothstep(0.0, 1.0, length(vWorldPos) / 1.0);
    vec3 base = mix(uOuterColor, uMidColor, radial);
    base = mix(base, uCenterColor, pow(radial, 3.0));
    base += n * 0.05;
    base += fres * 0.4 * uMidColor;
    base = mix(base, vec3(1.0), uRespawnProgress * 0.8);
    gl_FragColor = vec4(base, 1.0);
  }
`;

export class SpiritOrb {
  public group: THREE.Group;
  public mesh: THREE.Mesh;
  public material: THREE.ShaderMaterial;
  public light: THREE.PointLight;
  public lightTargetIntensity = 2.5;
  public basePos: THREE.Vector3;
  private breathPhase = 0;

  constructor() {
    this.basePos = new THREE.Vector3(...CONFIG.orb.position);
    this.group = new THREE.Group();
    this.group.position.copy(this.basePos);

    const geo = new THREE.IcosahedronGeometry(CONFIG.orb.baseRadius, 4);
    this.material = new THREE.ShaderMaterial({
      vertexShader: vert,
      fragmentShader: frag,
      uniforms: {
        uTime: { value: 0 },
        uCenterColor: { value: CONFIG.colors.orbCalmCenter.clone() },
        uMidColor: { value: CONFIG.colors.orbCalmMid.clone() },
        uOuterColor: { value: CONFIG.colors.orbCalmOuter.clone() },
        uCorruption: { value: 0 },
        uRespawnProgress: { value: 0 },
      },
      transparent: false,
    });
    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.castShadow = true;
    this.group.add(this.mesh);

    this.light = new THREE.PointLight(0xffd0a0, 2.5, 14, 1.5);
    this.group.add(this.light);

    const ringGeo = new THREE.RingGeometry(CONFIG.orb.baseRadius * 1.4, CONFIG.orb.baseRadius * 1.42, 64);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xfff8e8,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    this.group.add(ring);

    const outerRingGeo = new THREE.RingGeometry(CONFIG.orb.baseRadius * 1.8, CONFIG.orb.baseRadius * 1.82, 64);
    const outerRingMat = new THREE.MeshBasicMaterial({
      color: 0xfff8e8,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide,
    });
    const outerRing = new THREE.Mesh(outerRingGeo, outerRingMat);
    outerRing.rotation.x = Math.PI / 2;
    this.group.add(outerRing);
  }

  update(now: number, state: GameState, deltaMs: number): void {
    this.group.position.copy(this.basePos);
    this.material.uniforms.uTime.value = now * 0.001;
    this.breathPhase += deltaMs * 0.001 * (Math.PI * 2 / 3);
    const breath = 1.0 + Math.sin(this.breathPhase) * 0.04;
    this.mesh.scale.setScalar(breath);

    const ratio = state.corruption / CONFIG.corruption.max;
    const c = CONFIG.colors;
    const cc = c.orbCalmCenter.clone().lerp(c.orbCorruptCenter, ratio);
    const cm = c.orbCalmMid.clone().lerp(c.orbCorruptMid, ratio);
    const co = c.orbCalmOuter.clone().lerp(c.orbCorruptOuter, ratio);
    this.material.uniforms.uCenterColor.value.copy(cc);
    this.material.uniforms.uMidColor.value.copy(cm);
    this.material.uniforms.uOuterColor.value.copy(co);
    this.material.uniforms.uCorruption.value = ratio;

    if (state.isDead) {
      this.material.uniforms.uCenterColor.value.set('#1a0808');
      this.material.uniforms.uMidColor.value.set('#3a1010');
      this.material.uniforms.uOuterColor.value.set('#220808');
      this.material.uniforms.uRespawnProgress.value = 0;
      this.light.intensity = 0.15;
      this.light.color.set('#220000');
      this.mesh.scale.setScalar(0.7);
      return;
    }

    if (state.isRespawning) {
      const t = Math.min(1, (now - state.respawnStartedAt) / CONFIG.corruption.respawnDurationMs);
      this.material.uniforms.uRespawnProgress.value = t;
      this.light.intensity = this.lightTargetIntensity * (0.3 + 0.7 * t);
      let scaleMult: number;
      if (t < 0.3) {
        scaleMult = 1.0 + (t / 0.3) * 0.6;
      } else if (t < 0.55) {
        const localT = (t - 0.3) / 0.25;
        scaleMult = 1.6 - localT * 1.3;
      } else {
        const localT = (t - 0.55) / 0.45;
        scaleMult = 0.3 + localT * 0.7;
      }
      this.mesh.scale.setScalar(scaleMult * (1.0 + Math.sin(this.breathPhase) * 0.04));
    } else {
      this.material.uniforms.uRespawnProgress.value = 0;
      this.light.intensity = this.lightTargetIntensity * (1 + ratio * 1.2);
      this.light.color.copy(cm);
    }
  }

  position(): THREE.Vector3 {
    return this.basePos;
  }
}
